import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { setupAPI } from '../../services/api';

function SuperAdminSetup() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [dbForm, setDbForm] = useState({ host: '', port: '3306', user: '', password: '', database: '' });
  const [status, setStatus] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [initLogs, setInitLogs] = useState([]);
  const [migrateLogs, setMigrateLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('loading');

  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    setLoading(true);
    try {
      const { data } = await setupAPI.status();
      setStatus(data);
      if (data.dbConfig) {
        setDbForm({
          host: data.dbConfig.host || '',
          port: String(data.dbConfig.port || '3306'),
          user: data.dbConfig.user || '',
          password: '',
          database: data.dbConfig.database || '',
        });
      }
      if (!data.connected) {
        setStep('config');
      } else if (!data.tables || data.tables.length < 5) {
        setStep('init');
      } else {
        setStep('done');
      }
    } catch (err) {
      setStep('config');
    }
    setLoading(false);
  };

  const handleTest = async () => {
    setTestResult(null);
    try {
      const { data } = await setupAPI.testConnection(dbForm);
      setTestResult(data);
    } catch (err) {
      setTestResult({ ok: false, error: err.message });
    }
  };

  const handleSaveConfig = async () => {
    setLoading(true);
    try {
      await setupAPI.saveConfig(dbForm);
      setTestResult({ ok: true, message: 'Config saved.' });
    } catch (err) {
      setTestResult({ ok: false, error: err.message });
    }
    setLoading(false);
  };

  const handleInit = async () => {
    setLoading(true);
    setInitLogs([]);
    try {
      const { data } = await setupAPI.init();
      if (data.logs) setInitLogs(data.logs);
      if (data.ok) {
        setStep('done');
        loadStatus();
      } else {
        setInitLogs((prev) => [...prev, `ERROR: ${data.error}`]);
      }
    } catch (err) {
      setInitLogs([`ERROR: ${err.message}`]);
    }
    setLoading(false);
  };

  const handleLogout = () => { logout(); navigate('/login'); };

  const handleMigrate = async () => {
    setLoading(true);
    setMigrateLogs([]);
    try {
      const { data } = await setupAPI.migrate();
      if (data.logs) setMigrateLogs(data.logs);
      if (!data.ok) {
        setMigrateLogs((prev) => [...prev, `ERROR: ${data.error}`]);
      } else {
        loadStatus();
      }
    } catch (err) {
      setMigrateLogs([`ERROR: ${err.message}`]);
    }
    setLoading(false);
  };

  return (
    <div style={{ padding: '20px', fontFamily: 'Arial, sans-serif', maxWidth: '700px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1>Super Admin Setup</h1>
        <button onClick={handleLogout} style={{ padding: '8px 16px', background: '#dc3545', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Logout</button>
      </div>
      <p>Logged in as: <strong>{user?.name}</strong> ({user?.email})</p>

      {loading && <div style={{ background: '#d1ecf1', color: '#0c5460', padding: '12px', borderRadius: '4px', marginBottom: '15px' }}>Working...</div>}

      {/* DB Config Form */}
      <div style={{ background: '#fff', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', border: '1px solid #e0e0e0', marginBottom: '20px' }}>
        <h2 style={{ marginTop: 0 }}>Database Configuration</h2>
        {status && (
          <p style={{ fontSize: '13px', color: status.connected ? '#28a745' : '#dc3545', marginBottom: '15px' }}>
            Status: {status.connected ? 'Connected' : 'Not connected'}
          </p>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '15px' }}>
          <div>
            <label style={labelStyle}>Host</label>
            <input value={dbForm.host} onChange={(e) => setDbForm({...dbForm, host: e.target.value})} style={inputStyle} placeholder="mariadb" />
          </div>
          <div>
            <label style={labelStyle}>Port</label>
            <input value={dbForm.port} onChange={(e) => setDbForm({...dbForm, port: e.target.value})} style={inputStyle} placeholder="3306" />
          </div>
          <div>
            <label style={labelStyle}>User</label>
            <input value={dbForm.user} onChange={(e) => setDbForm({...dbForm, user: e.target.value})} style={inputStyle} placeholder="mariadb_user" />
          </div>
          <div>
            <label style={labelStyle}>Password</label>
            <input value={dbForm.password} onChange={(e) => setDbForm({...dbForm, password: e.target.value})} style={inputStyle} type="password" placeholder={status?.connected ? '(keep current)' : 'Enter password'} />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={labelStyle}>Database Name</label>
            <input value={dbForm.database} onChange={(e) => setDbForm({...dbForm, database: e.target.value})} style={inputStyle} placeholder="schoolbus_db" />
          </div>
        </div>

        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={handleTest} disabled={loading} style={btnStyle('#17a2b8')}>Test Connection</button>
          <button onClick={handleSaveConfig} disabled={loading} style={btnStyle('#ffc107')}>Save Config</button>
        </div>

        {testResult && (
          <div style={{ marginTop: '10px', padding: '10px', borderRadius: '4px', background: testResult.ok ? '#d4edda' : '#f8d7da', color: testResult.ok ? '#155724' : '#721c24', fontSize: '13px' }}>
            {testResult.ok ? testResult.message : testResult.error}
          </div>
        )}
      </div>

      {/* Database Init */}
      <div style={{ background: '#fff', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', border: '1px solid #e0e0e0', marginBottom: '20px' }}>
        <h2 style={{ marginTop: 0 }}>Database Initialization</h2>
        {status?.connected && status?.tables && (
          <div style={{ marginBottom: '15px', fontSize: '13px', color: '#555' }}>
            <p><strong>Tables:</strong> {status.tables.join(', ') || 'None'}</p>
            {status.migration && <p><strong>Migration:</strong> {status.migration.version}</p>}
            {status.seed && Object.keys(status.seed).length > 0 && (
              <p><strong>Seed data:</strong> {Object.entries(status.seed).map(([k, v]) => `${k}: ${v}`).join(', ')}</p>
            )}
          </div>
        )}

        <button onClick={handleInit} disabled={loading} style={btnStyle('#28a745')}>
          Initialize Database (Create Schema + Seed)
        </button>
        <p style={{ fontSize: '12px', color: '#888', marginTop: '8px' }}>
          This will create all tables, run UUID migration, and insert demo data if empty.
        </p>

        {initLogs.length > 0 && (
          <div style={{ marginTop: '15px', background: '#f8f9fa', padding: '12px', borderRadius: '4px', fontFamily: 'monospace', fontSize: '12px', maxHeight: '300px', overflowY: 'auto' }}>
            {initLogs.map((log, i) => (
              <div key={i} style={{ color: log.startsWith('WARN') ? '#856404' : log.startsWith('ERROR') ? '#dc3545' : '#333' }}>{log}</div>
            ))}
          </div>
        )}
      </div>

      {/* Migration */}
      <div style={{ background: '#fff', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', border: '1px solid #e0e0e0', marginBottom: '20px' }}>
        <h2 style={{ marginTop: 0 }}>Database Migration</h2>
        <p style={{ fontSize: '13px', color: '#555', marginBottom: '15px' }}>
          Apply pending migrations: add missing columns and tables (student_locations, photo_url, etc.)
        </p>

        <button onClick={handleMigrate} disabled={loading} style={btnStyle('#6f42c1')}>
          Apply Migrations
        </button>

        {migrateLogs.length > 0 && (
          <div style={{ marginTop: '15px', background: '#f8f9fa', padding: '12px', borderRadius: '4px', fontFamily: 'monospace', fontSize: '12px', maxHeight: '300px', overflowY: 'auto' }}>
            {migrateLogs.map((log, i) => (
              <div key={i} style={{ color: log.startsWith('ERROR') ? '#dc3545' : '#333' }}>{log}</div>
            ))}
          </div>
        )}
      </div>

      {step === 'done' && (
        <div style={{ background: '#d4edda', padding: '20px', borderRadius: '8px', border: '1px solid #c3e6cb', textAlign: 'center' }}>
          <h2 style={{ color: '#155724', marginTop: 0 }}>Setup Complete!</h2>
          <p style={{ color: '#155724' }}>Database is configured and ready. You can now log in with a regular admin account.</p>
          <p style={{ fontSize: '13px', color: '#555' }}>Default login: <strong>admin@demo.local</strong> / <strong>admin123</strong></p>
        </div>
      )}
    </div>
  );
}

const labelStyle = { display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 'bold', color: '#333' };
const inputStyle = { width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px', boxSizing: 'border-box' };
const btnStyle = (bg) => ({ padding: '8px 16px', background: bg, color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' });

export default SuperAdminSetup;
